import React, { useEffect, useRef } from 'react';
import { useBackHandler } from '../../contexts/NavigationBackContext';

/**
 * SwipeBackShell (FT-357)
 *
 * Shell-level wrapper that fires the currently-active `onBack` handler from
 * NavigationBackContext when the user performs an edge swipe from the left
 * side of the viewport. This is the standard iOS "swipe from left edge to
 * go back" gesture, applied globally to every mounted view instead of
 * per-screen.
 *
 * Design:
 *   - Attach touch listeners on the wrapper element itself (not `document`)
 *     so we play nicely with the app's existing scroll behaviour and only
 *     react to touches that begin within our subtree.
 *   - Reads the current back handler each frame via `useBackHandler()` so
 *     when a nested drill-down pushes its own handler onto the stack, the
 *     shell fires that one, then the parent's when the child unmounts, etc.
 *   - Uses a ref to hold the latest handler so the touch listeners don't
 *     need re-binding on every context update.
 *
 * Trigger conditions:
 *   - Touch starts within `EDGE_WIDTH_PX` of the left edge of the viewport.
 *   - Total horizontal delta ≥ `THRESHOLD_PX`.
 *   - Horizontal-dominant swipe (`deltaX > 2 * deltaY`) so vertical scrolls
 *     don't accidentally trigger.
 *   - Currently-active `onBack` handler is a function.
 *
 * Notes:
 *   - Per-screen `useSwipeBack` calls remain in place; the shell-level
 *     listener is additive, not a replacement.
 *   - Desktop is unaffected: touch events don't fire on desktop pointer
 *     input, and even if they did the CSS wrapper is transparent to layout.
 *   - We do NOT call `preventDefault` — this is a passive listener so we
 *     don't fight with intentional horizontal scroll (carousels, etc.);
 *     the swipe must originate at the edge to trigger.
 */

const EDGE_WIDTH_PX = 50;
const THRESHOLD_PX = 50;

export default function SwipeBackShell({ children }) {
  const onBack = useBackHandler();
  const wrapperRef = useRef(null);

  // Hold the latest onBack in a ref so listeners always read the current
  // handler without needing to re-bind whenever the context updates.
  const onBackRef = useRef(onBack);
  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return undefined;

    let startX = null;
    let startY = null;
    let edgeStart = false;

    const handleTouchStart = (e) => {
      const touch = e.touches && e.touches[0];
      if (!touch) return;
      startX = touch.clientX;
      startY = touch.clientY;
      edgeStart = touch.clientX <= EDGE_WIDTH_PX;
    };

    const handleTouchEnd = (e) => {
      if (startX == null || !edgeStart) {
        startX = null;
        startY = null;
        edgeStart = false;
        return;
      }
      const touch =
        (e.changedTouches && e.changedTouches[0]) ||
        (e.touches && e.touches[0]);
      if (touch) {
        const deltaX = touch.clientX - startX;
        const deltaY = Math.abs(touch.clientY - startY);
        // Horizontal-dominant right-swipe past the threshold, starting near
        // the left edge → fire the currently-active back handler.
        if (
          deltaX >= THRESHOLD_PX &&
          deltaX > 2 * deltaY &&
          typeof onBackRef.current === 'function'
        ) {
          try {
            onBackRef.current();
          } catch {
            /* swallow — back handlers must not crash the shell */
          }
        }
      }
      startX = null;
      startY = null;
      edgeStart = false;
    };

    // Passive listeners — we don't need to preventDefault. Also avoids
    // interfering with intentional horizontal scroll gestures in child
    // components.
    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });
    el.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchend', handleTouchEnd);
      el.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, []);

  return (
    <div ref={wrapperRef} data-testid="swipe-back-shell" className="contents">
      {children}
    </div>
  );
}
